import { Component, Input, ViewChild } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { TranslocoService } from "@ngneat/transloco";
import { concatMap, debounceTime, finalize, take } from "rxjs/operators";
import { from, Subject } from "rxjs";
import { faPlugCircleCheck } from "@fortawesome/free-solid-svg-icons";
import { SelectionModel } from "@angular/cdk/collections";
import { SceneMimeTypes } from "../mime-constants";
import { BlockBaseComponent } from "../block-base/block-base.component";
import { mimeSwap } from "../../../../common";
import {
    actionCRUDScene,
    actionRemoveSceneBlocks,
    actionSceneJumpLinked,
    actionUpdateSceneItem,
} from "../../../../store/actions/user.actions";
import { CommonService, ContainerService, LocalStoreService, StoreSelectorsService } from "../../../../services";
import { IFontFormat, ISceneBlock, ISceneItemsUpdate, ISchedule } from "../../../../interfaces";
import { DialogPopComponent } from "../../../../components/dialog-pop/dialog-pop.component";
import { Router } from "@angular/router";

@Component({
    selector: "app-block-scene",
    templateUrl: "./block-scene.component.html",
    styleUrls: ["./block-scene.component.scss"],
})

export class BlockSceneComponent extends BlockBaseComponent {
    mimeTypes = [];
    mimeType = "";
    faPlugCircleCheck = faPlugCircleCheck;
    accessSceneKey = "0";
    selectionModel: SelectionModel<any>;
    accessMask: Array<string> = [];
    selectedSchedule: string;
    schedules: Array<ISchedule> = [];
    isMimeMainPlayer: boolean;
    isPlayerAssignable = false;
    isSceneTopPropsMode = false;
    nonSelectedSchedule;
    isNestedScene = false;
    nestedSceneLabel = "";
    nestedSceneSrc = "";
    themes = [];
    theme = "1";
    selectedLabelBlocks: ISceneBlock[] = [];
    hasSelectedLabels = false;
    lastKnownFontColor = "";
    private fontChangeSubject = new Subject<{
        values: IFontFormat,
        i_field: string,
        i_componentName: string,
        addFontSlice: boolean
    }>();
    private reselectionTimeout: any = null;

    @ViewChild("fontSelector") fontSelector: any;

    @Input() set sceneTopProps(v: boolean) {
        this.isSceneTopPropsMode = v;
    }

    constructor(
        private translocoService: TranslocoService,
        public override storeSelector: StoreSelectorsService,
        private containerService: ContainerService,
        public override dialog: MatDialog,
        private commonService: CommonService,
        private router: Router,
        protected override localStoreService: LocalStoreService,
    ) {
        super(storeSelector, dialog);
        this.theme = this.localStoreService.getStorageValConfig("theme");

        this.nonSelectedSchedule = {
            schedule_name: this.translocoService.translate("general.nonScheduled", {}, this.translocoService.getActiveLang()),
            schedule_id: "-1",
            active: false,
        };

        Object.keys(SceneMimeTypes).forEach((k) => {
            this.mimeTypes.push(k);
        });
        this.themes = [
            {
                optionValue: "1000",
                optionsLabel: "Aeon (Desktop)",
            },
            {
                optionValue: "1010",
                optionsLabel: "Matal works (Desktop)",
            },
            {
                optionValue: "1020",
                optionsLabel: "Minimal (Desktop)",
            },
            {
                optionValue: "1060",
                optionsLabel: "Metal works",
            },
            {
                optionValue: "2070",
                optionsLabel: "Minimal",
            },
            {
                optionValue: "2080",
                optionsLabel: "Topcoat light",
            },
        ];
    }

    override preInit() {
        super.preInit();
        this.selectionModel = new SelectionModel<string>(true, [this.sceneId]);

        // Subscribe to active scene item IDs to detect selected label blocks
        this.cod(
            this.containerService.activeSceneItemIds.subscribe((itemIds) => {
                if (itemIds && itemIds.length > 0) {
                    this.updateSelectedLabelBlocks(itemIds);
                }
            }),
        );

        // Set up debounced font changes to prevent too many rapid updates
        this.cod(
            this.fontChangeSubject.pipe(
                debounceTime(300), // Wait 300ms after last change before processing
            ).subscribe(({ values, i_field, i_componentName, addFontSlice }) => {
                this.processFontChanges(values, i_field, i_componentName, addFontSlice);
            }),
        );

        this.cod(
            this.storeSelector.getSchedules().subscribe((i_items: ISchedule[]) => {
                this.schedules = i_items;
                this.selectedSchedule = this.sceneItem?.$?.scheduleId && this.sceneItem?.$?.scheduleId !== "-1" ? this.sceneItem.$.scheduleId : this.nonSelectedSchedule.schedule_id;
            }),
        );
        this.cod(
            this.storeSelector.getUserAccessMask().subscribe((v) => {
                this.accessMask = v;
                // this.accessSceneKey = this.sceneItem.$.accessKey;
            }),
        );
        if (this.sceneItem.$.src) {
            this.isNestedScene = true;
            this.nestedSceneSrc = this.sceneItem.$.src;
            this.cod(
                this.storeSelector.getScene(this.sceneItem.$.src)
                    .pipe(take(1))
                    .subscribe((val) => {
                        this.nestedSceneLabel = val.Player.$.label;
                    }),
            );
        }
    }

    changeAccessMask(v) {
        const sceneItemsUpdate: ISceneItemsUpdate = {
            sceneId: this.sceneId,
            sceneItemId: this.sceneId,
            values: [{
                key: "accessKey",
                value: v,
                storePath: "$",
            }],
        };
        this.dispatchSceneItemChanges(sceneItemsUpdate, false);
    }

    dropdownSchedulerChanged(v) {
        const sceneItemsUpdate: ISceneItemsUpdate = {
            sceneId: this.sceneId,
            sceneItemId: this.sceneId,
            values: [{
                key: "scheduleId",
                value: v.value,
                storePath: "$",
            }],
        };
        this.dispatchSceneItemChanges(sceneItemsUpdate, false);
    }

    togglePlayerAssignable(v) {
        const values = [{
            key: "mimeType",
            value: v.checked === true ? "mainPlayer" : "scene",
            storePath: "$",
        }];

        const sceneItemsUpdate: ISceneItemsUpdate = {
            sceneId: this.sceneId,
            sceneItemId: this.sceneId,
            values,
        };
        this.dispatchSceneItemChanges(sceneItemsUpdate, false);
    }

    override submit(formValues) {
        const storeMimeLong = this.sceneItem.$.mimeType;

        /** 4-11-2020 removed ability to change mime per scene, UNCOMMENT to bring back **/
        // const storeMimeShort = mimeSwap("longToShort", storeMimeLong);
        // const formMime = formValues.mimeType;

        /** 4-11-2020 removed ability to change mime per scene, COMMENT the following 3 lines to bring back **/
        formValues.mimeType = storeMimeLong;
        super.submit(formValues);

        if (!this.isNestedScene && formValues.label) {
            // update scheduler event value with new scene name
            this.commonService.updateScheduleEventValue("scene", [this.sceneId], formValues.label);
        }
        return;

        /** 4-11-2020 removed ability to change mime per scene, UNCOMMENT all the following lines to bring back **/
        //
        // /** no mime changed (other props have been modified) so keep mime as is **/
        // if (storeMimeShort === formMime || _.isUndefined(storeMimeLong) && formMime === 'None') {
        //     formValues.mimeType = storeMimeLong;
        //     super.submit(formValues);
        //     return;
        // }
        //
        // /** from a currently set mime in store to None **/
        // if (storeMimeLong && formMime === 'None') {
        //     const dialogRef = this.dialog.open(DialogPopComponent, {
        //         height: '300px',
        //         width: '600px',
        //         data: {name: `warning: are you sure you want to remove scene type? all related items will be removed, continue?`, showCancel: true}
        //     });
        //     this.cancelOnDestroy(
        //         dialogRef.afterClosed().subscribe(result => {
        //             if (!result) {
        //                 /** user changed mind, restore previous value **/
        //                 const originalShort = mimeSwap("longToShort", storeMimeLong);
        //                 this.setFormValue('mimeType', originalShort);
        //                 formValues.mimeType = storeMimeLong;
        //                 return super.submit(formValues);
        //             } else {
        //                 this.setFormValue('mimeType', 'None');
        //                 delete formValues.mimeType;
        //                 this.removeJsonItemsFromScene();
        //                 return super.submit(formValues);
        //             }
        //         })
        //     );
        //     return;
        // }
        //

        // /** from a currently set mime in store to another real mime type in form **/
        // if (!_.isUndefined(storeMimeShort) && formMime !== storeMimeShort) {
        //     const dialogRef = this.dialog.open(DialogPopComponent, {
        //         height: '300px',
        //         width: '600px',
        //         data: {name: `warning: are you sure you want to switch to a new scene type? all related items will be removed, continue?`, showCancel: true}
        //     });
        //     this.cancelOnDestroy(
        //         dialogRef.afterClosed().subscribe(result => {
        //             if (!result) {
        //                 /** user changed mind, restore previous value **/
        //                 const originalShort = mimeSwap("longToShort", storeMimeLong);
        //                 this.setFormValue('mimeType', originalShort);
        //                 formValues.mimeType = storeMimeLong;
        //                 return super.submit(formValues);
        //             } else {
        //                 formValues.mimeType = mimeSwap("shortToLong", formMime);
        //                 this.removeJsonItemsFromScene();
        //                 return super.submit(formValues);
        //             }
        //         })
        //     );
        //     return;
        // }
        //
        // /** from None to a mime type **/
        // if (_.isUndefined(storeMimeShort) && formMime) {
        //
        //     const dialogRef = this.dialog.open(DialogPopComponent, {
        //         height: '300px',
        //         width: '600px',
        //         data: {name: `warning: are you sure you want to switch to a new scene type? all related items will be removed, continue?`, showCancel: true}
        //     });
        //     this.cancelOnDestroy(
        //         dialogRef.afterClosed().subscribe(result => {
        //             if (!result) {
        //                 /** user changed mind, restore previous value **/
        //                 this.setFormValue('mimeType', 'None');
        //                 return super.submit(formValues);
        //             } else {
        //                 formValues.mimeType = mimeSwap("shortToLong", formMime);
        //                 this.removeJsonItemsFromScene();
        //                 return super.submit(formValues);
        //             }
        //         })
        //     );
        //     return;
        //
        //     // formValues.mimeType = mimeSwap("shortToLong", formMime);
        //     // return super.submit(formValues);
        //     // return;
        // }
    }

    override componentInit() {
        super.componentInit();
        if (this.sceneItem.$.mimeType) {
            /** 4-11-2020 removed ability to change mime per scene, UNCOMMENT to bring back **/
            // this.setUiConfigValue('mimeType', mimeSwap("longToShort", mimeType));
        } else {
            /** 4-11-2020 removed ability to change mime per scene, UNCOMMENT to bring back **/
            // this.setUiConfigValue('mimeType', 'None');
        }
    }

    override componentStoreUpdates() {
        if (this.sceneItem.$.mimeType === "mainPlayer" && this.isSceneTopPropsMode) {
            this.setUiConfigHide("label", true);
            this.setUiConfigHide("defaultDuration", true);
            this.setUiConfigHide("id", false);
            this.setUiConfigHide("src", false);
            this.setUiConfigHide("mode", false);
            this.setUiConfigHide("key", false);
        } else {
            this.setUiConfigHide("label", false);
            this.setUiConfigHide("defaultDuration", false);
            this.setUiConfigHide("id", true);
            this.setUiConfigHide("src", true);
            this.setUiConfigHide("mode", true);
            this.setUiConfigHide("key", true);
        }
        super.componentStoreUpdates();
        this.mimeType = mimeSwap("longToShort", this.sceneItem.$.mimeType);
        this.isMimeMainPlayer = this.sceneItem.$?.mimeType === SceneMimeTypes.Player || this.sceneItem.$?.mimeType === SceneMimeTypes.Scene || this.sceneItem.$?.mimeType?.length === 0;
        this.isPlayerAssignable = this.sceneItem.$?.mimeType === SceneMimeTypes.Player || this.sceneItem.$?.mimeType?.length === 0;
        super.initApplyStoreToDynamic(true);

        // Update font size field visibility after form is initialized
        setTimeout(() => {
            this.updateFontSizeFieldVisibility();
        }, 200);
    }

    override initRegisterDynamic() {
        this.uiConfigInit = [
            // {
            //     type: "select",
            //     label: this.translocoService.translate('general.sceneType', {}, this.translocoService.getActiveLang()),
            //     name: "mimeType",
            //     value: "Tech",
            //     options: this.mimeTypes,
            //     storePath: '$.mimeType'
            // },
            {
                type: "input",
                value: "scene",
                label: this.translocoService.translate("general.label", {}, this.translocoService.getActiveLang()),
                inputType: "text",
                name: "label",
                restrictChar: "basic2",
                storePath: "$.label",
            },
            {
                type: "checkbox",
                value: "0",
                label: this.translocoService.translate("general.kioskMode", {}, this.translocoService.getActiveLang()),
                inputType: "text",
                name: "mode",
                storePath: "Metadata[0].Kiosk[0].$.mode",
            },
            {
                type: "input",
                value: "password",
                label: this.translocoService.translate("general.kioskExitPassword2", {}, this.translocoService.getActiveLang()),
                inputType: "text",
                name: "key",
                restrictChar: "basic2",
                storePath: "Metadata[0].Kiosk[0].$.key",
            },
            /*
            {
                    type: "input",
                    value: "10",
                    label: this.translocoService.translate('general.defaultDuration', {}, this.translocoService.getActiveLang()),
                    inputType: "number",
                    name: "defaultDuration",
                    storePath: 'Data[0].Scene[0].$.defaultDuration'
            },
             */
            {
                type: "select",
                label: this.translocoService.translate("general.theme", {}, this.translocoService.getActiveLang()),
                name: "id",
                value: "1000",
                options: this.themes,
                hideMe: true,
                storePath: "Metadata[0].Theme[0].$.id",
            },
            {
                type: "select",
                label: this.translocoService.translate("general.softKeyboard", {}, this.translocoService.getActiveLang()),
                name: "src",
                value: "",
                options: [],
                observable: this.storeSelector.listenSceneOptions(),
                optionValue: "id",
                optionLabel: "label",
                defaultOption: { value: "", label: "None" },
                storePath: "Metadata[0].SoftKeyboard[0].Player[0].$.src",
            },
        ];

        if (this.isNestedScene) {
            delete this.uiConfigInit[0].storePath;
            // delete this.uiConfigInit[1].storePath;
        }
    }

    // assignIcon(i_blockPlayerId) {
    //     this.blockIcon = getFontIcons({$: {id: i_blockPlayerId}});
    // }

    removeJsonItemsFromScene() {
        this.sceneItem.Data[0].Scene[0].Players[0].Player.forEach((i_sceneItem) => {
            if (!this.storeSelector.isBlockIdJsonItemBased(i_sceneItem.$.player)) return;
            this.storeSelector.dispatch(actionRemoveSceneBlocks({
                    sceneId: this.sceneId,
                    sceneItemId: null,
                    values: [{
                        key: null,
                        value: i_sceneItem.$.id,
                        storePath: "Data[0].Scene[0].Players[0].Player",
                    }],
                }),
            );
        });
    }

    linkToNestedScene() {
        if (!this.nestedSceneSrc) return;
        this.storeSelector.dispatch(actionSceneJumpLinked({ previousLinkedSceneId: this.sceneId }));
        this.router.navigateByUrl(`/editor/scene/${this.nestedSceneSrc}`);
    }

    async convertAssignable() {
        const msg = this.translocoService.translate("message.sureConvert", {}, this.translocoService.getActiveLang());
        const dialogRef = this.dialog.open(DialogPopComponent, {
            height: "200px",
            width: "400px",
            data: { name: msg, showCancel: true },
        });
        dialogRef.afterClosed().subscribe(async (r) => {
            if (r) {
                this.storeSelector.dispatch(actionUpdateSceneItem({
                    sceneId: this.sceneItem.$.id,
                    sceneItemId: this.sceneItem.$.id,
                    values: [{
                        key: "mimeType",
                        value: SceneMimeTypes.Player,
                        storePath: "$",
                    }],
                    disableCRUD: true,
                }));
            }
        });
    }

    private updateSelectedLabelBlocks(selectedIds: string[]) {
        if (!selectedIds || selectedIds.length === 0) {
            this.selectedLabelBlocks = [];
            this.hasSelectedLabels = false;
            this.updateFontSizeFieldVisibility();
            return;
        }

        // Get all scene items from the current scene
        this.cod(
            this.storeSelector.getScene(this.sceneId).pipe(take(1)).subscribe((scene) => {
                if (!scene?.Player?.Data?.[0]?.Scene?.[0]?.Players?.[0]?.Player) {
                    this.selectedLabelBlocks = [];
                    this.hasSelectedLabels = false;
                    this.updateFontSizeFieldVisibility();
                    return;
                }
                const allSceneItems = scene.Player.Data[0].Scene[0].Players[0].Player;
                this.selectedLabelBlocks = allSceneItems.filter((item: ISceneBlock) => {
                    const isSelected = selectedIds.includes(item.$.id);
                    const isLabel = this.storeSelector.isBlockTypeLabelBased(item.$.player);
                    return isSelected && isLabel;
                });
                this.hasSelectedLabels = this.selectedLabelBlocks.length > 0;
                this.updateFontSizeFieldVisibility();

                // Set current font configuration if labels are selected
                if (this.hasSelectedLabels && this.selectedLabelBlocks.length > 0) {
                    this.updateFontSelectorConfig();
                }
            }),
        );
    }

    private updateFontSizeFieldVisibility() {
        // This method is kept for compatibility but doesn't do anything now
        // Font selector visibility is handled in the template
    }


    private updateFontSelectorConfig() {
        if (!this.hasSelectedLabels || !this.selectedLabelBlocks.length) return;

        // Get font configuration from first selected label
        const firstLabel = this.selectedLabelBlocks[0];
        const fontConfig = firstLabel?.Data?.[0]?.Label?.[0]?.Font?.[0]?.$;

        if (fontConfig && this.fontSelector) {
            // Create a mock scene block with the font configuration for the font-selector
            const mockConfig = {
                Data: [{
                    Label: [{
                        Font: [{
                            $: fontConfig,
                        }],
                    }],
                }],
            };

            setTimeout(() => {
                if (this.fontSelector) {
                    this.fontSelector.setConfig = { i_config: mockConfig, pickArray: 0 };
                    // Initialize color tracking
                    this.lastKnownFontColor = this.fontSelector.selectedFontColor || fontConfig.fontColor || "#000000";
                }
            }, 300);
        }
    }

    private hexToDecimal(h) {
        function hexfix(str) {
            let v, w;
            v = parseInt(str, 16);	// in rrggbb
            if (str.length === 3) {
                // nybble colors - fix to hex colors
                //  0x00000rgb              -> 0x000r0g0b
                //  0x000r0g0b | 0x00r0g0b0 -> 0x00rrggbb
                w = ((v & 0xF00) << 8) | ((v & 0x0F0) << 4) | (v & 0x00F);
                v = w | (w << 4);
            }
            return v.toString(16).toUpperCase();
        }

        let h2 = h.replace(/#/gi, "");
        h2 = hexfix(h2);
        return parseInt(h2, 16);
    }

    onMultiFonChanged(values: IFontFormat, i_field: string, i_componentName: string, addFontSlice: boolean = false) {
        if (this.selectedLabelBlocks.length === 0) {
            return;
        }

        // Clear any existing timeout
        if (this.reselectionTimeout) {
            clearTimeout(this.reselectionTimeout);
            this.reselectionTimeout = null;
        }

        // Feed the subject to trigger debounced processing
        this.fontChangeSubject.next({ values, i_field, i_componentName, addFontSlice });
    }

    private processFontChanges(values: IFontFormat, i_field: string, i_componentName: string, addFontSlice: boolean = false) {
        if (this.selectedLabelBlocks.length === 0) {
            return;
        }

        // Process each label block sequentially using RxJS
        const processLabelBlock = (labelBlock: ISceneBlock) => {
            const changes = [];
            ["fontSize", "fontColor", "fontFamily", "fontWeight", "fontStyle", "textDecoration", "textAlign", "alignSelf", "autoScaling", "wrap"].forEach((k) => {
                let value = values[k];

                // Convert hex color to decimal format for fontColor
                if (k === "fontColor" && value && typeof value === "string" && value.startsWith("#")) {
                    value = this.hexToDecimal(value).toString();
                }

                changes.push({
                    key: k,
                    value: value,
                    storePath: i_field === "" ? `Data[0].${i_componentName}[0].Font[0].$` : `Data[0].${i_componentName}[0].${i_field}[0].Font[0].$`,
                });
            });

            if (addFontSlice) {
                // Convert fontColor to decimal for addFontSlice as well
                const fontSliceValues = { ...values };
                if (fontSliceValues.fontColor && typeof fontSliceValues.fontColor === "string" && fontSliceValues.fontColor.startsWith("#")) {
                    fontSliceValues.fontColor = this.hexToDecimal(fontSliceValues.fontColor).toString();
                }

                this.dispatchSceneItemChanges({
                    sceneId: this.sceneId, sceneItemId: labelBlock.$.id, values: [{
                        key: "Font",
                        value: [{ $: fontSliceValues }],
                        storePath: `Data[0].${i_componentName}[0]`,
                    }],
                });
            }
            this.dispatchSceneItemChanges({ sceneId: this.sceneId, sceneItemId: labelBlock.$.id, values: changes });

            // Wait for the store to update before continuing
            return this.storeSelector.listenPlayerDataOfSelectedSceneItemId().pipe(take(1));
        };

        // Store the current selection IDs for re-selection
        const currentSelectionIds = this.selectedLabelBlocks.map(block => block.$.id);

        // Create sequential chain of updates
        const updateChain = from(this.selectedLabelBlocks).pipe(
            concatMap(labelBlock => processLabelBlock(labelBlock)),
            finalize(() => {
                // Clear selection to avoid jumping handles and buttons
                this.containerService.activeSceneItemIds.next([]);

                // Trigger scene rebuild to refresh visual components
                this.storeSelector.dispatch(actionCRUDScene({
                    data: {
                        command: "rebuild",
                        sceneId: this.sceneId,
                        itemIds: [],
                    },
                }));

                // Clear any existing timeout
                if (this.reselectionTimeout) {
                    clearTimeout(this.reselectionTimeout);
                }

                // Set up 2-second delay for re-selection
                this.reselectionTimeout = setTimeout(() => {
                    this.reselectLabelBlocks(currentSelectionIds);
                }, 333);
            }),
        );

        this.cod(updateChain.subscribe());
    }

    private reselectLabelBlocks(labelIds: string[]) {
        if (!labelIds || labelIds.length === 0) return;

        // Re-trigger selection in scene-builder via activeSceneItemIds
        this.containerService.activeSceneItemIds.next(labelIds);
    }
}

